import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { SelectOption } from '@fcom/common';

export interface PageIndexes {
  startIndex: number;
  endIndex: number;
}

@Component({
  selector: 'fcom-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaginationComponent implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  totalItems = input<number>();
  textBeforePages = input<string>();
  itemsPerPageOptions = input<number[]>([4, 8, 16]);
  itemsPerPage = signal(0);
  indexesChange = output<PageIndexes>();

  readonly options = computed<SelectOption[]>(() =>
    this.itemsPerPageOptions().map((value) => ({ name: `${value}`, value: `${value}` }))
  );

  readonly currentPage = signal(1);
  readonly totalPages = computed(() => Math.ceil((this.totalItems() ?? 0) / this.itemsPerPage()));
  readonly startIndex = computed(() => (this.currentPage() - 1) * this.itemsPerPage());
  readonly endIndex = computed(() => this.startIndex() + this.itemsPerPage());
  reactiveForm: UntypedFormGroup;

  readonly FIRST_PAGE = 1;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  constructor() {
    effect(() => {
      this.reactiveForm?.get('paginationCount').setValue(`${this.itemsPerPage()}`, { emitEvent: false });
    });

    effect(() => {
      this.totalItems();
      untracked(() => this.reset());
    });

    effect(() => {
      const options = this.itemsPerPageOptions();
      this.itemsPerPage.set(options[0]);
    });

    effect(() => {
      this.indexesChange.emit({
        startIndex: this.startIndex(),
        endIndex: this.endIndex(),
      });
    });
  }

  ngOnInit(): void {
    this.createForm();

    this.reactiveForm
      .get('paginationCount')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.itemsPerPage.set(+value);
        this.reset();
      });
  }

  reset(): void {
    this.changePage(this.FIRST_PAGE);
  }

  private createForm(): void {
    this.reactiveForm = this.fb.group({
      paginationCount: [this.options()[0].value],
    });
  }

  prevPagination(): void {
    this.changePage(this.currentPage() - 1);
  }

  nextPagination(): void {
    this.changePage(this.currentPage() + 1);
  }

  private changePage(newPageNumber: number): void {
    const clamped = Math.max(this.FIRST_PAGE, Math.min(newPageNumber, this.totalPages()));
    this.currentPage.set(clamped);
  }
}
